/**
 * Determines the color of the score bar based on mastery percentage
 * @param mastery - The mastery percentage (0-1 or 0-100)
 * @param lowThreshold - The low threshold percentage (default: 30)
 * @param highThreshold - The high threshold percentage (default: 70)
 * @param getLowColor - Function to get the low color (default: red)
 * @param getHighColor - Function to get the high color (default: green)
 * @param getDefaultColor - Function to get the default color (default: blue)
 * @returns Color string
 */
export const getMasteryColor = (
  mastery: number,
  lowThreshold: number = 30,
  highThreshold: number = 70,
  getLowColor: () => string = () => '#F44336',
  getHighColor: () => string = () => '#4CAF50',
  getDefaultColor: () => string = () => '#4786FC',
): string => {
  // Convert to percentage if it's a decimal (0-1)
  const percentage = getMasteryPercentage(mastery);

  if (percentage >= highThreshold) {
    return getHighColor(); // Green
  }
  if (percentage <= lowThreshold) {
    return getLowColor(); // Red
  }
  return getDefaultColor(); // Blue
};

export const getMasteryPercentage = (mastery: number) => {
  return mastery <= 1 ? mastery * 100 : mastery;
};

/**
 * Extracts student names from an array of items (skills, lessons, etc.) that contain students
 * @param items - Array of items with students property (e.g., Skill[], Lesson[])
 * @returns Map of student_id to student_name
 */
export const extractStudentNames = <
  T extends { students?: Array<{ student_id?: string; student_name?: string }> },
>(
  items: T[] | undefined,
): { [studentId: string]: string } => {
  const studentNameMap: { [studentId: string]: string } = {};

  if (items && Array.isArray(items)) {
    items.forEach((item) => {
      if (item.students && Array.isArray(item.students)) {
        item.students.forEach((student) => {
          const name = student.student_name?.trim();
          if (student.student_id && name) {
            studentNameMap[student.student_id] = name;
          }
        });
      }
    });
  }

  return studentNameMap;
};
